import { FormattedMessage } from 'react-intl';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Box, Typography } from '@mui/material';
import isWebGLSupported from '@ui-framework/helpers/isWebGLSupported';

const MapSupportCheckContext = createContext(null);

export function useMapSupportCheck() {
  const context = useContext(MapSupportCheckContext);

  if (!context) {
    throw new Error('useMapSupportCheck must be used within MapSupportCheck');
  }

  return context;
}

function MapSupportCheckUnsupported({ error }) {
  return (
    <Box p={2}>
      <Alert severity="warning">
        <Typography variant="subtitle1" gutterBottom>
          <FormattedMessage
            id="MAP_CANNOT_BE_DISPLAYED"
            defaultMessage="Map cannot be displayed"
          />
        </Typography>
        <Typography variant="body2">{error}</Typography>
        <ul>
          <li>
            <FormattedMessage
              id="ENABLE_HARDWARE_ACCELERATION_IN_YOUR_BROWSER_SETTINGS"
              defaultMessage="Enable hardware acceleration in your browser settings."
            />
          </li>
          <li>
            <FormattedMessage
              id="UPDATE_YOUR_BROWSER_TO_THE_LATEST_VERSION"
              defaultMessage="Update your browser to the latest version."
            />
          </li>
          <li>
            <FormattedMessage
              id="TRY_CHROME_EDGE_FIREFOX_OR_SAFARI"
              defaultMessage="Try Chrome, Edge, Firefox, or Safari."
            />
          </li>
          <li>
            <FormattedMessage
              id="DISABLE_GRAPHICS_RELATED_BROWSER_EXTENSIONS"
              defaultMessage="Disable graphics-related browser extensions."
            />
          </li>
          <li>
            <FormattedMessage
              id="RESTART_YOUR_BROWSER_AFTER_CHANGING_SETTINGS"
              defaultMessage="Restart your browser after changing settings."
            />
          </li>
          <li>
            <FormattedMessage
              id="CITRIX_HELPER_INSTRUCTION"
              defaultMessage="If you are using Citrix, VMware, Remote Desktop, or a managed corporate device, hardware acceleration may be disabled by policy. Contact your IT administrator if the issue persists."
            />
          </li>
        </ul>
      </Alert>
    </Box>
  );
}

function MapSupportCheckProvider({ children }) {
  const [status, setStatus] = useState({
    checking: true,
    supported: false,
    error: null,
  });

  const onError = useCallback(nextStatus => {
    setStatus(nextStatus);
  }, []);

  useEffect(() => {
    try {
      const supported = isWebGLSupported();

      if (!supported) {
        setStatus({
          checking: false,
          supported: false,
          error:
            'Your browser does not support the WebGL features required to display this map.',
        });
        return;
      }

      setStatus({
        checking: false,
        supported: true,
        error: null,
      });
    } catch (err) {
      setStatus({
        checking: false,
        supported: false,
        error: err?.message || 'Unable to initialize map support check.',
      });
    }
  }, []);

  const value = useMemo(() => ({ status, onError }), [status, onError]);

  if (status.checking) {
    return (
      <MapSupportCheckContext.Provider value={value}>
        <Box p={2}>
          <Typography>
            <FormattedMessage
              id="CHECKING_MAP_SUPPORT"
              defaultMessage="Checking map support"
            />
            ...
          </Typography>
        </Box>
      </MapSupportCheckContext.Provider>
    );
  }

  if (!status.supported) {
    return (
      <MapSupportCheckContext.Provider value={value}>
        <MapSupportCheckUnsupported error={status.error} />
      </MapSupportCheckContext.Provider>
    );
  }

  return (
    <MapSupportCheckContext.Provider value={value}>
      {children}
    </MapSupportCheckContext.Provider>
  );
}

export default MapSupportCheckProvider;
